// SeparationPage.tsx
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, RootDispatch } from "../../store";
import { fetchUserNames } from "../../store/slices/userSlice";
import {
  fetchEmployeeDetails,
  updateEmployeeField,
} from "../../store/slices/employeeSlice";
import { fetchAttendanceMetadata } from "../../store/slices/attendanceSlice";
import EditableField from "./EditableField";

interface SeparationData {
  separationMode: string;
  resignationSubmittedOn: string;
  reasonForLeaving: string;
  noticeRequired: boolean;
  shortFallInNoticePeriod: number;
  tentativeLeavingDate: string;
  excludedFromFinalSettlement: boolean;
  exitInterviewDate: string;
  exitInterviewRemarks: string;
  leavingDate: string;
  settledOn: string;
  alternativeEmailId: string;
  noticeServed: boolean;
  hasLeftOrganization: boolean;
  altMobileNumber?: string;
  exitRemarks?: string;
  date?: string;
  noticePeriod?: number;
}

const defaultState: SeparationData = {
  separationMode: "",
  resignationSubmittedOn: "",
  reasonForLeaving: "",
  noticeRequired: false,
  shortFallInNoticePeriod: 0,
  noticePeriod: 0,
  tentativeLeavingDate: "",
  excludedFromFinalSettlement: false,
  exitInterviewDate: "",
  exitInterviewRemarks: "",
  leavingDate: "",
  settledOn: "",
  alternativeEmailId: "",
  noticeServed: false,
  hasLeftOrganization: false,
};

const SeparationPage: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const users = useSelector((state: RootState) => state.users.allUsersNames);
  const employeeDetails = useSelector(
    (state: RootState) => state.employee.userDetails
  );
  const metadata = useSelector(
    (state: RootState) => state.attendance.attendanceMetadata
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState<typeof users>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [form, setForm] = useState<SeparationData>(defaultState);

  const resignationModes = metadata?.resignationModes || [];

  useEffect(() => {
    dispatch(fetchUserNames());
    dispatch(fetchAttendanceMetadata());
  }, [dispatch]);

  useEffect(() => {
    if (selectedUserId) {
      dispatch(fetchEmployeeDetails(selectedUserId));
    }
  }, [dispatch, selectedUserId]);

  useEffect(() => {
    if (employeeDetails) {
      setForm({ ...defaultState, ...employeeDetails });
    }
  }, [employeeDetails]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    let results = [...users];
    if (value.trim() !== "") {
      results = results.filter((user) =>
        user.username.toLowerCase().includes(value.toLowerCase())
      );
    }

    results.sort((a, b) => a.username.localeCompare(b.username));
    setFilteredUsers(results);
  };

  const handleUserSelect = (userId: string, username: string) => {
    setSelectedUserId(userId);
    setSearchTerm(username);
    setFilteredUsers([]);
    setForm(defaultState);
  };

  const handleFieldUpdate = async (updatedField: Record<string, any>) => {
    if (!selectedUserId) return;

    const fieldKey = Object.keys(updatedField)[0];
    const value = updatedField[fieldKey];

    const updatesToSend: Record<string, any> = { [fieldKey]: value };

    const newForm = { ...form, ...updatesToSend };

    const resignationSubmittedOn = newForm.resignationSubmittedOn
      ? new Date(newForm.resignationSubmittedOn)
      : null;
    const noticePeriod = newForm.noticePeriod || 0;
    const leavingDate = newForm.leavingDate
      ? new Date(newForm.leavingDate)
      : null;

    if (
      resignationSubmittedOn &&
      noticePeriod &&
      (fieldKey === "resignationSubmittedOn" || fieldKey === "noticePeriod")
    ) {
      const tentativeDate = new Date(resignationSubmittedOn);
      tentativeDate.setDate(tentativeDate.getDate() + noticePeriod);
      updatesToSend.tentativeLeavingDate = tentativeDate.toISOString();
    }

    if (
      ["resignationSubmittedOn", "noticePeriod", "leavingDate"].includes(
        fieldKey
      ) &&
      resignationSubmittedOn &&
      noticePeriod &&
      leavingDate
    ) {
      const expectedLeaving = new Date(resignationSubmittedOn);
      expectedLeaving.setDate(expectedLeaving.getDate() + noticePeriod);

      updatesToSend.shortFallInNoticePeriod = Math.max(
        0,
        Math.ceil(
          (expectedLeaving.getTime() - leavingDate.getTime()) /
            (1000 * 60 * 60 * 24)
        )
      );
    }

    setForm((prev) => ({ ...prev, ...updatesToSend }));

    for (const [key, val] of Object.entries(updatesToSend)) {
      let valToSend = val;
      if (
        [
          "tentativeLeavingDate",
          "resignationSubmittedOn",
          "exitInterviewDate",
          "leavingDate",
          "settledOn",
          "date",
        ].includes(key)
      ) {
        if (val) valToSend = new Date(val).toISOString();
      }

      await dispatch(
        updateEmployeeField({
          userId: selectedUserId,
          fieldKey: key,
          value: valToSend,
        })
      );
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-md p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
        Employee Separation / Resignation
      </h2>

      {/* Search Bar */}
      <div className="mb-4 relative max-w-xs">
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={() => {
            if (!selectedUserId) {
              const allUsers = [...users].sort((a, b) =>
                a.username.localeCompare(b.username)
              );
              setFilteredUsers(allUsers);
            }
          }}
          placeholder="Search employee..."
          disabled={!!selectedUserId}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none"
        />{" "}
        {/* X button to clear selected user */}
        {selectedUserId && (
          <button
            onClick={() => {
              setSelectedUserId(null);
              setSearchTerm("");
              setFilteredUsers([]);
            }}
            className="absolute right-3 top-3.5 text-gray-400 hover:text-red-500 text-lg"
          >
            &#10005;
          </button>
        )}
        {filteredUsers.length > 0 && (
          <ul className="absolute z-10 bg-white border border-gray-300 w-full mt-1 max-h-40 overflow-y-auto rounded shadow">
            {filteredUsers.map((user) => (
              <li
                key={user._id}
                className="px-3 py-2 cursor-pointer hover:bg-blue-50"
                onClick={() => handleUserSelect(user._id, user.username)}
              >
                {user.username}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Separation Mode */}
      {selectedUserId && (
        <div className="max-w-xs">
          <EditableField
            label="Separation Mode"
            fieldKey="separationMode"
            value={form.separationMode}
            onUpdate={handleFieldUpdate}
            metadata={{ resignationModes }}
            optionsKey="resignationModes"
            type="select"
          />
        </div>
      )}

      {/* Resigned Sections */}
      {selectedUserId && form.separationMode === "resigned" && (
        <div className="space-y-6 mt-4">
          <div className="p-4 bg-white shadow rounded-lg">
            <h3 className="font-semibold mb-2">Resignation Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <EditableField
                label="Resignation Submitted On"
                fieldKey="resignationSubmittedOn"
                value={form.resignationSubmittedOn?.slice(0, 10) || ""}
                onUpdate={handleFieldUpdate}
                type="date"
              />
              <EditableField
                label="Reason For Leaving"
                fieldKey="reasonForLeaving"
                value={form.reasonForLeaving}
                onUpdate={handleFieldUpdate}
                type="text"
              />
              <EditableField
                label="Notice Required"
                fieldKey="noticeRequired"
                value={form.noticeRequired}
                onUpdate={handleFieldUpdate}
                type="checkbox"
              />
              <EditableField
                label="Short Fall In Notice Period"
                fieldKey="shortFallInNoticePeriod"
                value={form.shortFallInNoticePeriod ?? 0}
                onUpdate={() => {}}
                type="number"
              />
              <EditableField
                label="Notice Period"
                fieldKey="noticePeriod"
                value={form.noticePeriod}
                onUpdate={handleFieldUpdate}
                type="number"
              />
              <EditableField
                label="Tentative Leaving Date"
                fieldKey="tentativeLeavingDate"
                value={form.tentativeLeavingDate?.slice(0, 10) || ""}
                onUpdate={() => {}}
                type="date"
              />
            </div>
          </div>

          <div className="p-4 bg-white shadow rounded-lg">
            <h3 className="font-semibold mb-2">Exit Interview</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <EditableField
                label="Exit Interview Date"
                fieldKey="exitInterviewDate"
                value={form.exitInterviewDate?.slice(0, 10) || ""}
                onUpdate={handleFieldUpdate}
                type="date"
              />
              <EditableField
                label="Exit Interview Remarks"
                fieldKey="exitInterviewRemarks"
                value={form.exitInterviewRemarks}
                onUpdate={handleFieldUpdate}
                type="text"
              />
            </div>
          </div>

          <div className="p-4 bg-white shadow rounded-lg">
            <h3 className="font-semibold mb-2">Exit Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <EditableField
                label="Leaving Date"
                fieldKey="leavingDate"
                value={form.leavingDate?.slice(0, 10) || ""}
                onUpdate={handleFieldUpdate}
                type="date"
              />
              <EditableField
                label="Settled On"
                fieldKey="settledOn"
                value={form.settledOn?.slice(0, 10) || ""}
                onUpdate={handleFieldUpdate}
                type="date"
              />
              <EditableField
                label="Has Left Organization"
                fieldKey="hasLeftOrganization"
                value={form.hasLeftOrganization}
                onUpdate={handleFieldUpdate}
                type="checkbox"
              />
              <EditableField
                label="Notice Served"
                fieldKey="noticeServed"
                value={form.noticeServed}
                onUpdate={handleFieldUpdate}
                type="checkbox"
              />
              <EditableField
                label="Excluded From Final Settlement"
                fieldKey="excludedFromFinalSettlement"
                value={form.excludedFromFinalSettlement}
                onUpdate={handleFieldUpdate}
                type="checkbox"
              />
              <EditableField
                label="Alternative Email ID"
                fieldKey="alternativeEmailId"
                value={form.alternativeEmailId}
                onUpdate={handleFieldUpdate}
                type="text"
              />

              <EditableField
                label="Exit Remarks"
                fieldKey="exitRemarks"
                value={form.exitRemarks}
                onUpdate={handleFieldUpdate}
                type="text"
              />
              <EditableField
                label="Alt Mobile Number"
                fieldKey="altMobileNumber"
                value={form.altMobileNumber}
                onUpdate={handleFieldUpdate}
                type="text"
              />
            </div>
          </div>
        </div>
      )}

      {/* Other Separation Details */}
      {selectedUserId &&
        form.separationMode &&
        form.separationMode !== "resigned" && (
          <div className="p-4 bg-white shadow rounded-lg mt-4">
            <h3 className="font-semibold mb-2">Other Separation Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <EditableField
                label="date"
                fieldKey="date"
                value={form.date?.slice(0, 10) || ""}
                onUpdate={handleFieldUpdate}
                type="date"
              />
              <EditableField
                label="Alternative Email ID"
                fieldKey="alternativeEmailId"
                value={form.alternativeEmailId}
                onUpdate={handleFieldUpdate}
                type="text"
              />
              <EditableField
                label="Exit Remarks"
                fieldKey="exitRemarks"
                value={form.exitRemarks}
                onUpdate={handleFieldUpdate}
                type="text"
              />
              <EditableField
                label="Alt Mobile Number"
                fieldKey="altMobileNumber"
                value={form.altMobileNumber}
                onUpdate={handleFieldUpdate}
                type="text"
              />
            </div>
          </div>
        )}
    </div>
  );
};

export default SeparationPage;
